import React, { useState, useEffect, useRef } from 'react';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import {
  startAuthentication,
} from '@simplewebauthn/browser';
import { apiUrl } from '../config/api';
import './Login.css';

// ---------------------------------------------------------------------------
// PIN digit-box input
// ---------------------------------------------------------------------------
const PIN_LENGTH = 4;

const PinInput = ({ value, onChange, disabled, autoFocus }) => {
  const inputs = useRef([]);

  const handleKey = (e, idx) => {
    if (e.key === 'Backspace') {
      if (value[idx]) {
        const next = value.split(''); next[idx] = ''; onChange(next.join(''));
      } else if (idx > 0) {
        inputs.current[idx - 1]?.focus();
        const next = value.split(''); next[idx - 1] = ''; onChange(next.join(''));
      }
      e.preventDefault();
    } else if (/^\d$/.test(e.key)) {
      const next = value.split(''); next[idx] = e.key; onChange(next.join(''));
      if (idx < PIN_LENGTH - 1) inputs.current[idx + 1]?.focus();
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, PIN_LENGTH);
    onChange(pasted.padEnd(PIN_LENGTH, '').slice(0, PIN_LENGTH));
    inputs.current[Math.min(pasted.length, PIN_LENGTH - 1)]?.focus();
    e.preventDefault();
  };

  return (
    <div className="pin-boxes">
      {Array.from({ length: PIN_LENGTH }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="password"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          className={`pin-box${value[i] ? ' pin-box--filled' : ''}`}
          value={value[i] || ''}
          onChange={() => {}}
          onKeyDown={(e) => handleKey(e, i)}
          onPaste={handlePaste}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          autoComplete="off"
          aria-label={`PIN digit ${i + 1}`}
        />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Biometric icon (generic — works for Face ID and fingerprint)
// ---------------------------------------------------------------------------
const BiometricIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M12 2C9.38 2 7 3.56 7 6c0 1.1.45 2.1 1.17 2.83"/>
    <path d="M17 6c0-2.44-2.38-4-5-4"/>
    <path d="M7 10a5 5 0 0 0 5 5"/>
    <path d="M17 10a5 5 0 0 1-2.26 4.23"/>
    <path d="M12 15v4"/>
    <path d="M8 21h8"/>
    <path d="M5 7.5A8.96 8.96 0 0 0 3 13c0 2.21.8 4.23 2.12 5.79"/>
    <path d="M19 7.5c1.25 1.56 2 3.54 2 5.5 0 2.21-.8 4.23-2.12 5.79"/>
  </svg>
);

// ---------------------------------------------------------------------------
// Detect if the browser supports WebAuthn platform authenticators
// (Face ID, Touch ID, Windows Hello, Android fingerprint, etc.)
// ---------------------------------------------------------------------------
async function checkBiometricAvailable() {
  if (
    typeof window === 'undefined' ||
    !window.PublicKeyCredential ||
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== 'function'
  ) {
    return false;
  }
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Main Login component
//
// Screens:
//   'password'  — email + password
//   'pin-setup' — first login: set a 4-digit PIN
//   'pin'       — returning user: enter PIN (+ biometric button if registered)
// ---------------------------------------------------------------------------
const Login = ({ onLoginSuccess }) => {
  const [screen, setScreen] = useState('password');
  const [bypassPin, setBypassPin] = useState(false);

  // Password screen
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // PIN screens
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [pendingUser, setPendingUser] = useState(null);
  const [pinEmail, setPinEmail] = useState('');

  // Biometric
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // ── On mount ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) return;

    const storedEmail = localStorage.getItem('userEmail');
    const pinSet = localStorage.getItem('pinSet') === 'true';
    if (storedEmail && pinSet) {
      setPinEmail(storedEmail);
      setScreen('pin');
      // Check biometric availability once we know we're showing the PIN screen
      checkBiometricAvailable().then((available) => {
        if (available) {
          // Also check that this user actually has a passkey registered
          // by calling the auth-challenge endpoint silently.
          // We just set the flag here; the challenge is only fetched on button tap.
          setBiometricAvailable(true);
        }
      });
    }
  }, []);

  const clearError = () => setError('');

  // ── Password login ────────────────────────────────────────────────────────
  const handlePasswordLogin = async (e) => {
    e?.preventDefault();
    if (!email || !password) { setError('Email and password are required'); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || data.message || 'Login failed'); return; }

      if (bypassPin && data.pinSet) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('userEmail', data.user.email);
        onLoginSuccess(data.user);
        return;
      }

      if (!data.pinSet) {
        setPendingToken(data.token); setPendingUser(data.user);
        setPinEmail(data.user.email); setPin(''); setPinConfirm('');
        setScreen('pin-setup');
      } else {
        setPendingToken(data.token); setPendingUser(data.user);
        setPinEmail(data.user.email); setPin('');
        setScreen('pin');
        checkBiometricAvailable().then(setBiometricAvailable);
      }
    } catch { setError('Failed to connect to server.'); }
    finally { setLoading(false); }
  };

  // ── Set PIN (first login) ─────────────────────────────────────────────────
  const handleSetPin = async (e) => {
    e?.preventDefault();
    if (pin.length < PIN_LENGTH) { setError(`Enter all ${PIN_LENGTH} digits`); return; }
    if (pin !== pinConfirm) { setError("PINs don't match — try again"); setPinConfirm(''); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/auth/set-pin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${pendingToken}` },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || data.message || 'Could not set PIN'); return; }
      localStorage.setItem('token', pendingToken);
      localStorage.setItem('user', JSON.stringify(pendingUser));
      localStorage.setItem('userEmail', pendingUser.email);
      localStorage.setItem('pinSet', 'true');
      onLoginSuccess(pendingUser);
    } catch { setError('Failed to connect to server.'); }
    finally { setLoading(false); }
  };

  // ── PIN login ─────────────────────────────────────────────────────────────
  const handlePinLogin = async (submittedPin) => {
    const p = submittedPin ?? pin;
    if (p.length < PIN_LENGTH) return;
    setError(''); setLoading(true);
    try {
      const res = await fetch(apiUrl('/api/auth/login-pin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: pinEmail, pin: p }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 400) {
          // Server has no PIN on file (e.g. an admin cleared it) even though this
          // browser cached pinSet=true — drop the stale cache and fall back to
          // password login so the user gets routed through PIN setup again.
          localStorage.removeItem('pinSet');
          setPin('');
          setEmail(pinEmail);
          setScreen('password');
          return;
        }
        setError(data.detail || data.message || 'Invalid PIN'); setPin(''); return;
      }
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('userEmail', data.user.email);
      localStorage.setItem('pinSet', 'true');
      onLoginSuccess(data.user);
    } catch { setError('Failed to connect to server.'); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (screen === 'pin' && pin.length === PIN_LENGTH) handlePinLogin(pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, screen]);

  // ── Biometric login ───────────────────────────────────────────────────────
  const handleBiometricLogin = async () => {
    setError(''); setBiometricLoading(true);
    try {
      // 1. Get challenge from server
      const challengeRes = await fetch(apiUrl('/api/webauthn/auth/challenge'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client-Origin': window.location.origin },
        body: JSON.stringify({ email: pinEmail }),
      });
      if (!challengeRes.ok) {
        const d = await challengeRes.json().catch(() => ({}));
        // 404 means no passkey registered for this account — hide the button
        if (challengeRes.status === 404) { setBiometricAvailable(false); return; }
        setError(d.detail || d.message || 'Could not start biometric login');
        return;
      }
      const challengeOptions = await challengeRes.json();

      // 2. Trigger browser/OS biometric prompt
      let credential;
      try {
        credential = await startAuthentication(challengeOptions);
      } catch (authErr) {
        // User cancelled or no credential matched — fall back silently to PIN
        if (authErr.name === 'NotAllowedError') return;
        throw authErr;
      }

      // 3. Verify with server and get JWT
      const verifyRes = await fetch(apiUrl('/api/webauthn/auth/verify'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Client-Origin': window.location.origin },
        body: JSON.stringify({ email: pinEmail, credential }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok) {
        setError(verifyData.detail || verifyData.message || 'Biometric verification failed');
        return;
      }

      localStorage.setItem('token', verifyData.token);
      localStorage.setItem('user', JSON.stringify(verifyData.user));
      localStorage.setItem('userEmail', verifyData.user.email);
      localStorage.setItem('pinSet', 'true');
      onLoginSuccess(verifyData.user);
    } catch (err) {
      console.error('Biometric login error:', err);
      setError('Biometric login failed. Try your PIN instead.');
    } finally {
      setBiometricLoading(false);
    }
  };

  // ── Back to password ──────────────────────────────────────────────────────
  const goBackToPassword = () => {
    setBypassPin(true); setScreen('password');
    setPin(''); setPinConfirm(''); setPendingToken(''); setPendingUser(null); setError('');
  };

  // ── Shared left panel ─────────────────────────────────────────────────────
  const leftPanel = (
    <div className="login-left">
      <img src="/image.png" alt="" className="login-boss-photo" />
      <div className="login-left-overlay" />
      <div className="login-left-inner">
        <div className="login-brand">
          <img src="/logo-mark.png" alt="" className="login-brand-mark" />
          <div className="login-brand-text">
            <span className="login-brand-name">Capturify</span>
            <span className="login-brand-suffix">by ChimneySweeps</span>
          </div>
        </div>
        <div className="login-tagline">Manage your inspection photos with precision.</div>
        <div className="login-features-box">
          <ul className="login-features-list">
            <li><span className="feature-check">✓</span>Scrape &amp; organise CompanyCam projects in seconds</li>
            <li><span className="feature-check">✓</span>Upload and annotate images per client</li>
            <li><span className="feature-check">✓</span>Role-based user management for your team</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const mobileBrand = (showTagline = false) => (
    <div className="login-mobile-brand pin-mobile-brand">
      <img src="/image.png" alt="" className="login-boss-photo login-boss-photo--mobile" />
      <div className="login-mobile-brand-overlay" />
      <div className="login-brand" style={{ position: 'relative', zIndex: 2 }}>
        <img src="/logo-mark.png" alt="" className="login-brand-mark" />
        <div className="login-brand-text">
          <span className="login-brand-name">Capturify</span>
          <span className="login-brand-suffix">by ChimneySweeps</span>
        </div>
      </div>
      {showTagline && <p className="login-mobile-tagline">Manage your inspection photos with precision.</p>}
    </div>
  );

  // ── Render: password screen ───────────────────────────────────────────────
  if (screen === 'password') {
    return (
      <div className="login-root">
        {leftPanel}
        <div className="login-right">
          <div className="login-form-wrap">
            {mobileBrand(true)}
            <div className="login-form-card">
              <div className="login-form-header">
                <h1 className="login-heading">Welcome back</h1>
                <p className="login-sub">Sign in to your account</p>
              </div>
              {error && <div className="login-error" role="alert">{error}</div>}
              <form onSubmit={handlePasswordLogin} className="login-form" noValidate>
                <div className="lf-group">
                  <label htmlFor="email" className="lf-label">Email address</label>
                  <input id="email" type="email" className="lf-input"
                    value={email} onChange={(e) => { setEmail(e.target.value); clearError(); }}
                    placeholder="you@company.com" required disabled={loading}
                    autoComplete="email" autoFocus />
                </div>
                <div className="lf-group">
                  <label htmlFor="password" className="lf-label">Password</label>
                  <div className="lf-password-wrap">
                    <input id="password" type={showPassword ? 'text' : 'password'}
                      className="lf-input lf-input--password"
                      value={password} onChange={(e) => { setPassword(e.target.value); clearError(); }}
                      placeholder="Enter your password" required disabled={loading}
                      autoComplete="current-password" />
                    <button type="button" className="lf-eye-btn"
                      onClick={() => setShowPassword(!showPassword)} disabled={loading}
                      tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <AiOutlineEyeInvisible size={19} /> : <AiOutlineEye size={19} />}
                    </button>
                  </div>
                </div>
                <button type="submit" className="lf-submit-btn" disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: PIN setup screen ──────────────────────────────────────────────
  if (screen === 'pin-setup') {
    const setupReady = pin.length === PIN_LENGTH && pinConfirm.length === PIN_LENGTH;
    return (
      <div className="login-root">
        {leftPanel}
        <div className="login-right">
          <div className="login-form-wrap">
            {mobileBrand(false)}
            <div className="login-form-card">
              <div className="login-form-header">
                <h1 className="login-heading">Create your PIN</h1>
                <p className="login-sub">You'll use this {PIN_LENGTH}-digit PIN to sign in going forward.</p>
              </div>
              {error && <div className="login-error" role="alert">{error}</div>}
              <form onSubmit={handleSetPin} className="login-form" noValidate>
                <div className="lf-group">
                  <label className="lf-label">Choose a {PIN_LENGTH}-digit PIN</label>
                  <PinInput value={pin} onChange={(v) => { setPin(v); clearError(); }} disabled={loading} autoFocus />
                </div>
                <div className="lf-group" style={{ marginTop: 8 }}>
                  <label className="lf-label">Confirm PIN</label>
                  <PinInput value={pinConfirm} onChange={(v) => { setPinConfirm(v); clearError(); }} disabled={loading} />
                </div>
                <button type="submit" className="lf-submit-btn"
                  disabled={loading || !setupReady} style={{ marginTop: 20 }}>
                  {loading ? 'Saving…' : 'Set PIN & Enter'}
                </button>
              </form>
              <div className="pin-back-row">
                <button type="button" className="pin-back-link" onClick={goBackToPassword}>
                  ← Back to sign in
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: PIN entry screen (with optional biometric button) ─────────────
  const anyLoading = loading || biometricLoading;
  return (
    <div className="login-root">
      {leftPanel}
      <div className="login-right pin-right">
        <div className="pin-screen-wrap">
          {mobileBrand(false)}
          <div className="pin-card">

            {/* Avatar */}
            <div className="pin-avatar" aria-hidden="true">
              {pinEmail.charAt(0).toUpperCase()}
            </div>

            <h1 className="pin-card-heading">Welcome back</h1>
            <p className="pin-card-email">{pinEmail}</p>

            {error && <div className="login-error pin-error" role="alert">{error}</div>}

            {/* ── Biometric button — shown when device supports it AND passkey is registered ── */}
            {biometricAvailable && (
              <button
                type="button"
                className={`pin-biometric-btn${biometricLoading ? ' pin-biometric-btn--loading' : ''}`}
                onClick={handleBiometricLogin}
                disabled={anyLoading}
                aria-label="Sign in with Face ID or fingerprint"
              >
                <BiometricIcon />
                <span>{biometricLoading ? 'Verifying…' : 'Use Face ID / Fingerprint'}</span>
              </button>
            )}

            {/* ── Separator ── */}
            {biometricAvailable && (
              <div className="pin-or-divider"><span>or enter PIN</span></div>
            )}

            {/* ── PIN boxes ── */}
            <div className="pin-input-row">
              <PinInput
                value={pin}
                onChange={(v) => { setPin(v); clearError(); }}
                disabled={anyLoading}
                autoFocus={!biometricAvailable}
              />
            </div>

            <p className="pin-status">
              {loading ? 'Verifying…' : `Enter your ${PIN_LENGTH}-digit PIN`}
            </p>

            <div className="pin-separator"><span>or</span></div>

            <button type="button" className="pin-password-btn"
              onClick={goBackToPassword} disabled={anyLoading}>
              Sign in with password
            </button>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
